import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Color, MeshPhongMaterial, Quaternion, Vector3 } from "three";
import { addVectors, getForwardVector, getLookAtRotation, makeTransform, projectVectorOntoPlane, setQuaternion, vectorForward, vectorRight, vectorTimes, vectorUp } from "../../../core/util/SpatialUtils";
import { Model, setMaterialRecursively, useModelTemplate } from "../../../core/util/useModel";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useContact3DPlayerRecords } from "../../../../game/contact3d/replicatedObjects/Contact3DPlayerRecords";
import { getPlayerColor } from "../../../core/util/PlayerColors";
import { ColorHSV } from "../../../core/util/ColorHSV";
import { PlaneFitter } from "../../../core/util/PlaneFitter";
import { usePoolFunction } from "../../util/UsePoolFunction";
import { useGameState } from "../../../../game/contact3d/replicatedObjects/GameState";
import { WATER_CENTER } from "./Contact3DBackgroundObjects";
import { useUpdate } from "../../../core/util/UseUpdate";
import { DomText } from "../../../core/components/DomText";
import { Font } from "../../../core/components/TextElement";
import { toCssColorString } from "../../../core/util/ColorPreset";
const EPSILON = 0.0001;
const BASE_Y = -0.55;
const HALF_NUM_SAMPLES = 2;
const SAMPLE_DIST = 0.025;
const BOB_ACCELERATION = 1;
const BOB_BACK_ACCELERATION_FACTOR = 10;
const BOB_DRAG = 0.05;
const BACK_POW = 0.75;
const TEXT_Y_OFFSET = vectorTimes(vectorUp(), 1.75);
const XZ_ACCELERATION = 0.5;
const XZ_DRAG = 0.5;
const START_MOVING_DIST = 0.33;
const STOP_MOVING_DIST = 0.33;
const SWIM_STRENGTH = 2;
const SWIM_APPLY_DURATION = 0.25;
const SWIM_DELAY = 0.75;
const Y_ACCELERATION = 6.5;
const Y_DRAG = 0.475;
export const Contact3DThreeJSPlayer = (props) => {
    const { record, playerApiRef } = props;
    const playerTemplate = useModelTemplate('/models/figure.glb');
    const color = useMemo(() => pastelize(getPlayerColor(record.index)), []);
    const playerModelRef = useRef(undefined);
    const materialRef = useRef(undefined);
    const sampleY = usePoolFunction();
    const playerRecords = useContact3DPlayerRecords();
    const { secretWordGiverId } = useGameState();
    useEffect(() => {
        const material = new MeshPhongMaterial({ color });
        materialRef.current = material;
        if (playerModelRef.current !== undefined) {
            setMaterialRecursively(playerModelRef.current, material);
        }
        return () => {
            material.dispose();
        };
    }, []);
    useEffect(() => {
        if (materialRef.current === undefined) {
            return;
        }
        materialRef.current.color = new Color(color);
    }, [color]);
    const onCreateInstance = useCallback((model) => {
        if (materialRef.current !== undefined) {
            setMaterialRecursively(model, materialRef.current);
        }
    }, []);
    const frameState = useRef({
        lookAtPosition: new Vector3(0, 0, 0),
        basePosition: new Vector3(0, 0, 0),
        tiltVector: new Vector3(),
        tiltVectorVelocity: new Vector3(0, 0, 0),
        xzVelocity: new Vector3(),
        yVelocity: 0,
        swimTimer: 0,
        swimApplyTimer: 0.5,
        isMoving: true,
    });
    const targetPosition = useMemo(() => {
        let index;
        let length;
        if (secretWordGiverId !== undefined && record.playerId === secretWordGiverId) {
            index = -1;
            length = 1;
        }
        else {
            length = Object.values(playerRecords.record).length;
            index = record.index;
            if (secretWordGiverId !== undefined) {
                const secretWordGiverRecord = playerRecords.record[secretWordGiverId];
                if (secretWordGiverRecord !== undefined) {
                    length--;
                    if (index > secretWordGiverRecord.index) {
                        index--;
                    }
                }
            }
        }
        const { basePosition, targetPosition } = getPoolCirclePosition(index, length);
        frameState.current.basePosition = basePosition;
        return targetPosition;
    }, [record, playerRecords, secretWordGiverId]);
    useEffect(() => {
        if (playerApiRef !== undefined) {
            playerApiRef.current = {
                applyXZTiltForce: (force) => applyXZTiltForce(frameState.current, force),
                applyYForce: (force) => applyYForce(frameState.current, force),
                getPosition: () => { return playerModelRef.current ? playerModelRef.current.position : new Vector3(); },
            };
        }
    }, []);
    const apiRef = useRef(undefined);
    useUpdate((state, deltaTime) => {
        updateObject(frameState.current, targetPosition, playerModelRef.current, sampleY, apiRef.current, deltaTime);
    });
    const initialTransform = makeTransform({
        scale: new Vector3(0.5, 0.5, 0.5),
    });
    return _jsxs(_Fragment, { children: [_jsx(Model, { initialTransform: initialTransform, template: playerTemplate, modelRef: playerModelRef, onCreateInstance: onCreateInstance }), _jsx(DomText, { apiRef: apiRef, text: `${record.username}: ${record.score}`, font: Font.Inter, fontSize: 220, color1: toCssColorString(color) })] });
};
function pastelize(color) {
    const hsv = ColorHSV.fromHex(color);
    hsv.s /= 1.5;
    return hsv.toHex();
}
function updateObject(frameState, targetPosition, figureObject, sampleY, textApi, deltaTime) {
    if (figureObject === undefined) {
        return;
    }
    targetPosition.y = figureObject.position.y;
    applyXZSwimming(frameState, targetPosition, figureObject, deltaTime);
    applyWaterYForce(frameState, figureObject, sampleY, deltaTime);
    applyWaterForces(frameState, figureObject, sampleY, deltaTime);
    setOrientation(frameState, figureObject, deltaTime);
    textApi === null || textApi === void 0 ? void 0 : textApi.setPosition(addVectors(figureObject.position, TEXT_Y_OFFSET));
}
function applyXZSwimming(frameState, targetPosition, figureObject, deltaTime) {
    if (!frameState.isMoving && targetPosition.distanceTo(figureObject.position) > START_MOVING_DIST) {
        frameState.isMoving = true;
    }
    const diff = new Vector3().subVectors(targetPosition, figureObject.position);
    const distance = diff.length();
    const targetDir = diff.clone().normalize();
    if (frameState.isMoving && distance > EPSILON) {
        frameState.swimTimer -= deltaTime;
        if (frameState.swimTimer <= 0) {
            frameState.swimTimer = SWIM_DELAY;
            frameState.swimApplyTimer = SWIM_APPLY_DURATION;
        }
        if (frameState.swimApplyTimer >= 0) {
            frameState.swimApplyTimer -= deltaTime;
            const swimTimeDelta = deltaTime + Math.min(0, frameState.swimApplyTimer);
            frameState.xzVelocity.addScaledVector(targetDir, SWIM_STRENGTH * swimTimeDelta);
        }
        if (figureObject.position.distanceTo(targetPosition) < STOP_MOVING_DIST) {
            frameState.isMoving = false;
        }
    }
    figureObject.position.addScaledVector(frameState.xzVelocity, deltaTime);
}
function setOrientation(frameState, figureObject, deltaTime) {
    const lookRotation = getLookRotation(frameState, figureObject);
    setQuaternion(figureObject.quaternion, lookRotation);
    const baseVector = getForwardVector(figureObject);
    const tiltedVector = getForwardVector(figureObject);
    const tiltVectorNorm = frameState.tiltVector.clone().normalize();
    const tiltVectorFacingRotation = new Quaternion().setFromUnitVectors(vectorRight(), tiltVectorNorm);
    const tiltVectorZRotation = new Quaternion().setFromAxisAngle(vectorForward(), frameState.tiltVector.length());
    const reverseTiltVectorFacingRotation = tiltVectorFacingRotation.clone().invert();
    const applyFinalRotations = (vector) => {
        vector.applyQuaternion(reverseTiltVectorFacingRotation);
        vector.applyQuaternion(tiltVectorZRotation);
        vector.applyQuaternion(tiltVectorFacingRotation);
    };
    applyFinalRotations(tiltedVector);
    const upVector = vectorUp();
    applyFinalRotations(upVector);
    const projectedBaseVector = projectVectorOntoPlane(baseVector, upVector);
    const reverseUpRotation = new Quaternion().setFromUnitVectors(upVector, vectorUp());
    projectedBaseVector.applyQuaternion(reverseUpRotation);
    tiltedVector.applyQuaternion(reverseUpRotation);
    const correctiveRotation = new Quaternion().setFromUnitVectors(tiltedVector, projectedBaseVector);
    figureObject.applyQuaternion(correctiveRotation);
    applyFinalRotations(figureObject);
}
function applyWaterYForce(frameState, figureObject, sampleY, deltaTime) {
    const waterY = sampleY(figureObject.position.x, figureObject.position.z) + BASE_Y;
    frameState.yVelocity += (waterY - figureObject.position.y) * deltaTime * Y_ACCELERATION;
    frameState.yVelocity *= Math.pow(Y_DRAG, deltaTime);
    figureObject.position.addScaledVector(vectorUp(), frameState.yVelocity * deltaTime);
}
function applyYForce(frameState, forceAmount) {
    frameState.yVelocity += forceAmount;
}
function applyXZTiltForce(frameState, force) {
    frameState.tiltVectorVelocity.add(force);
}
function applyWaterForces(frameState, figureObject, sampleY, deltaTime) {
    const samples = [];
    for (let i = -HALF_NUM_SAMPLES; i <= HALF_NUM_SAMPLES; i++) {
        for (let j = -HALF_NUM_SAMPLES; j <= HALF_NUM_SAMPLES; j++) {
            const pos = new Vector3(figureObject.position.x + i * SAMPLE_DIST, 0, figureObject.position.z + j * SAMPLE_DIST);
            pos.y = sampleY(pos.x, pos.z);
            samples.push(pos);
        }
    }
    const upVector = PlaneFitter.fitPlane(samples);
    if (upVector.y < 0) {
        upVector.multiplyScalar(-1);
    }
    const s = upVector.cross(vectorUp()).length();
    const c = upVector.dot(vectorUp());
    const angle = Math.atan2(s, c);
    upVector.y = 0;
    upVector.normalize();
    const addVelocity = upVector.clone();
    addVelocity.multiplyScalar(angle * deltaTime);
    const subVelocity = frameState.tiltVector.clone().normalize();
    subVelocity.multiplyScalar(-1 * Math.pow(frameState.tiltVector.length(), BACK_POW) * deltaTime * BOB_BACK_ACCELERATION_FACTOR);
    frameState.tiltVectorVelocity.addScaledVector(addVelocity, BOB_ACCELERATION);
    frameState.tiltVectorVelocity.add(subVelocity);
    frameState.tiltVectorVelocity.multiplyScalar(Math.pow(BOB_DRAG, deltaTime));
    frameState.tiltVector.addScaledVector(frameState.tiltVectorVelocity, deltaTime);
    frameState.xzVelocity.addScaledVector(addVelocity, XZ_ACCELERATION);
    frameState.xzVelocity.multiplyScalar(Math.pow(XZ_DRAG, deltaTime));
}
function getLookRotation(frameState, figureObject) {
    frameState.lookAtPosition.y = figureObject.position.y;
    frameState.basePosition.y = figureObject.position.y;
    if (figureObject.position.distanceTo(frameState.lookAtPosition) > EPSILON) {
        return getLookAtRotation(figureObject, frameState.lookAtPosition);
    }
    else if (figureObject.position.distanceTo(frameState.basePosition) > EPSILON) {
        return getLookAtRotation(figureObject, frameState.basePosition);
    }
    return new Quaternion();
}
function getPoolCirclePosition(index, length) {
    const angle = -Math.PI / 6 + (Math.PI * 2 / length) * index;
    const circleRadius = 4.5;
    const basePosition = WATER_CENTER.clone();
    const position = basePosition.clone();
    if (index >= 0)
        position.add(new Vector3(Math.cos(angle) * circleRadius, 0, -Math.sin(angle) * circleRadius));
    return {
        basePosition,
        targetPosition: position
    };
}
//# sourceMappingURL=Contact3DThreeJSPlayer.js.map